import React from "react";
import IntegrationConversionSettings from "./IntegrationConversionSettings";
import {IntegrationAdAccountList, IntegrationAdAccountRow} from "./IntegrationAdAccounts";
import {Box, Button, DialogActions, DialogContent, List, StepConnector, TextField, useMediaQuery, useTheme, Step, StepLabel, Stepper, Alert, Link} from "@mui/material";
import {IntegrationDialog as Dialog, IntegrationHeader, IntegrationBackButton} from "./IntegrationDesign";
import {integrationActions} from "../../../store/actions/integrationAction";
import {connect} from "react-redux";
import {useCallback, useEffect, useState} from "react";
import Spinner from "../../spinner/Spinner";
import {Check} from "@mui/icons-material";
import {useFieldArray, useForm} from "react-hook-form";
import {integrationService} from "../../../service/integrationService";
import {alertActions} from "../../../store/actions/alertActions";
import CopyContentToClipboard from "../../utils/CopyContentToClipboard";
import {CustomListItem, CustomListItemIcon, CustomListItemText} from "./RevContentIntegration";
import ReactHookFormSelect from "../../input/ReactHookFormSelect";

function SmartNewsIntegration(props) {
    const theme = useTheme();
    const {
        integration, resetIntegration, error, smartNewsConnect, find, setActiveStep,
        getSmartNewsAdAccounts, integrateSmartNewsAdAccount, integrateSmartNewsConversionAPI,
        removeIntegrationEvent
    } = props;

    const steps = ["Connect", "Integrate Ad Account", "Conversion API"];

    const integrationId = parseInt(props?.match?.params?.id);

    const [open, setOpen] = useState(true);
    const [removeEventIndex, setRemoveEventIndex] = useState(null);
    const [removePixelIndex, setRemovePixelIndex] = useState(null);
    const [adAccountId, setAdAccountId] = useState(null);

    const {register, handleSubmit, control, setValue, watch, formState: {errors}, getValues} = useForm();

    const {fields, remove, append} = useFieldArray({control, name: "events"});

    const {fields: pixelFields, remove: pixelRemove, append: pixelAppend} = useFieldArray(
        {control, name: "pixels"}
    );

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        resetIntegration();
        setOpen(false);
        props.history.push({pathname: "/settings/integrations", state: {from: props.location}});
    }, [resetIntegration, props.history, props.location]);

    // Step 1: Connect with SmartNews.
    const handleOnSubmitStep1 = (data) => {
        if (integration?.currentIntegration === null) {
            smartNewsConnect({businessId: data?.businessId}); // connect only if integration not exist.
        } else {
            setActiveStep(2);
        }
    };

    // Step 2: Save/Update ad account data to DB.
    const handleOnSubmitStep2 = () => {
        let businessId = "";
        for (const cred of integration?.currentIntegration?.integrationCredentials) {
            if (cred?.keyName === "smartnews_business_id") {
                businessId = cred?.keyValue;
            }
        }

        integrateSmartNewsAdAccount({
            adAccountId: parseInt(getValues("adAccountId")),
            adAccountName: getValues("adAccountName"),
            currency: getValues("currency"),
            timezone: getValues("timezone"),
            businessId: parseInt(businessId),
            integrationId: integration?.currentIntegration?.id
        });
    };

    // Step 3: Integrate Conversion API with events and pixels.
    const handleOnSubmit = (data) => {

        // Modify pixels dataset.
        let pixels = [];
        data?.pixels?.forEach((pixel) => {
            pixels?.push({
                id: pixel?.rowId,
                name: pixel?.name,
                pixelId: pixel?.pixelId,
                pixelAccessToken: pixel?.pixelAccessToken
            })
        });
        data.pixels = pixels;

        // Modify events dataset.
        let events = [];
        data?.events?.forEach((event) => {
            events?.push({
                id: event?.eventId,
                name: event?.name,
                includePayout: event?.includePayout
            })
        });
        data.events = events;

        // If conversion API is FALSE then set events and pixels to 0
        if (!data?.enableConversionAPI) {
            data.events = [];
            data.pixels = [];
        }

        // Set integration id
        if (integrationId === 0 || integrationId === null || integrationId === undefined || !integrationId) {
            data.integrationId = integration?.currentIntegration?.id;
        } else {
            data.integrationId = integrationId;
        }

        integrateSmartNewsConversionAPI({
            integrationId: data.integrationId,
            enableConversionAPI: data?.enableConversionAPI,
            events: data.events,
            pixels: data.pixels
        });
    };

    // Step 3: Close integration form when everything is successfully submitted.
    useEffect(() => {
        if (integration?.smartnews?.conversionApiSuccess) {
            handleClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleClose, integration.smartnews.conversionApiSuccess]);

    // Step 3: Add event.
    const appendNewEvent = () => {
        append({
            eventId: 0,
            name: "",
            includePayout: false
        });
    };

    const handleDeleteIntegrationEvent = useCallback(({eventId, index}) => {
        if (eventId === 0) {
            remove(index); // from list
            return false;
        }
        setRemoveEventIndex({eventId, index}); // set event to remove from list and do API req.
    }, [remove, setRemoveEventIndex]);

    useEffect(() => {
        if (removeEventIndex !== null) {
            remove(removeEventIndex?.index); // from list
            removeIntegrationEvent({integrationId, eventId: removeEventIndex?.eventId}); // API req
        }
    }, [integrationId, remove, removeEventIndex, removeIntegrationEvent]);

    // Step 3: Add pixel.
    const appendNewPixel = () => {
        pixelAppend({
            rowId: 0,
            name: "",
            pixelId: "",
            pixelAccessToken: ""
        });
    };

    // Step 3: Delete pixel from DB.
    const handleDeleteIntegrationPixel = useCallback(({pixelId, index}) => {
        if (pixelId === 0) {
            pixelRemove(index);
            return false;
        }

        integrationService.deleteIntegrationPixel({integrationId, pixelId}).then(() => {
            setRemovePixelIndex(index);
        }).catch((e) => {
            error(e?.response?.data?.error);
        });
    }, [integrationId, error, pixelRemove]);

    // Step 3: Remove pixel form list.
    useEffect(() => {
        if (removePixelIndex !== null) {
            pixelRemove(removePixelIndex);
        }
    }, [pixelRemove, removePixelIndex])

    // Step 1: Map data
    // Step 2: Map data
    useEffect(() => {
        if (integration?.smartnews?.activeStep === 1 && integrationId > 0 && integration?.currentIntegration !== null) {
            if (integration?.currentIntegration?.id === integrationId) {
                for (const cred of integration?.currentIntegration?.integrationCredentials) {
                    if (cred?.keyName === "smartnews_business_id") {
                        setValue("businessId", cred?.keyValue);
                    }
                }
            }
        }

        if (integration?.smartnews?.activeStep === 2 && integrationId > 0 && integration?.currentIntegration !== null) {
            if (integration?.currentIntegration?.id === integrationId) {
                setValue("name", integration?.currentIntegration?.networkName);
                for (const cred of integration?.currentIntegration?.integrationCredentials) {
                    if (cred?.keyName === "smartnews_ad_account_id") {
                        setValue("adAccountId", cred?.keyValue);
                    }
                    if (cred?.keyName === "smartnews_ad_account_currency") {
                        setValue("currency", cred?.keyValue);
                    }
                    if (cred?.keyName === "smartnews_ad_account_timezone") {
                        setValue("timezone", cred?.keyValue);
                    }
                    if (cred?.keyName === "smartnews_ad_account_id") {
                        setValue("adAccountId", cred?.keyValue);
                    }
                    if (cred?.keyName === "smartnews_ad_account_name") {
                        setValue("adAccountName", cred?.keyValue);
                    }
                }
            }
        }
    }, [integration?.smartnews?.activeStep, setValue, integration?.currentIntegration, integrationId]);

    // Step 2: Get Ad Accounts.
    useEffect(() => {
        if (integration?.smartnews?.activeStep === 2 && integration?.currentIntegration !== null) {

            let businessId = "";
            for (const cred of integration?.currentIntegration?.integrationCredentials) {
                if (cred?.keyName === "smartnews_business_id") {
                    businessId = cred?.keyValue;
                }
            }

            getSmartNewsAdAccounts({
                integrationId: integration?.currentIntegration?.id,
                businessId: businessId
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integration?.smartnews?.activeStep, getSmartNewsAdAccounts, integration?.smartnews?.businessId, integration?.smartnews?.integrationId]);

    // Step 3: Conversion API
    useEffect(() => {
        if (integration?.smartnews?.activeStep === 3 && integration?.integrations !== null) {
            for (const i of integration?.integrations) {
                if (i?.id === integrationId) {
                    for (const cred of i?.integrationCredentials) {
                        if (cred?.keyName === "smartnews_enable_conversion_api") {
                            if (cred?.keyValue === "true") {
                                setValue("enableConversionAPI", true);
                            } else {
                                setValue("enableConversionAPI", false);
                            }
                        }
                    }

                    if (getValues("events")?.length === 0) {
                        let events = [];
                        i?.integrationEvents?.forEach((event) => {
                            events.push({
                                eventId: event?.id,
                                name: event?.name,
                                includePayout: event?.includePayout
                            });
                        });
                        setValue("events", events);
                    }

                    let pixels = [];
                    i?.integrationPixels?.forEach((pixel) => {
                        pixels.push({
                            rowId: pixel?.id,
                            name: pixel?.name,
                            pixelId: pixel?.pixelId,
                            pixelAccessToken: pixel?.pixelAccessToken,
                        });
                    });
                    setValue("pixels", pixels);
                    break;
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integration?.smartnews?.activeStep])

    // Step 1: Find integration if edit mode.
    useEffect(() => {
        if (integration?.smartnews?.activeStep === 1 && integrationId > 0 && integration?.currentIntegration === null) {
            find(integrationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integrationId]);

    const developerAppID = "102968777";

    return (
        <Dialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"sm"}
                aria-labelledby="smartnews-ad-account-integration"
        >
            <IntegrationHeader name="SmartNews" logo="/assets/images/traffic-sources/smartnews-white.svg" id="smartnews-ad-account-integration"/>
            <DialogContent>
                <Stepper
                    alternativeLabel={fullScreen}
                    activeStep={integration?.smartnews?.activeStep - 1}
                    connector={<StepConnector sx={{
                        "& .MuiStepConnector-line": {
                            borderColor: "background.tableBorder",
                            borderTopWidth: 2,
                        },
                    }}/>}
                    sx={{
                        margin: "0 auto",
                        width: "100%",
                        maxWidth: 800,
                        padding: "0px 24px 24px 24px"
                    }}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {integration?.smartnews?.activeStep === 1 &&
                    <>
                        <Box>

                            {integration?.loading?.integration && <Spinner overComponentBox={true}/>}

                            <Alert severity="info" variant="filled">
                                This is required to grant access to your
                                reporting data.
                            </Alert>

                            <p>
                                Please check our integration
                                guide <Link target="_blank" underline="hover" rel="noreferrer"
                                            href="https://skro.eu/docs/integrations/smartnews-integration">here</Link>.
                            </p>

                            <p>
                                Navigate to SmartNews Business Manager:
                            </p>
                            <ul>
                                <li>
                                    Go to <strong>SmartNews</strong> → <strong>Business Manager</strong> → <strong>Developer
                                    Apps</strong> → <strong>App Access Management</strong>.
                                </li>
                                <li>Add the provided <strong>Developer App ID</strong> to grant access.</li>
                            </ul>

                            <CopyContentToClipboard
                                label={"Developer App ID"}
                                copyText={developerAppID}
                                showStartAdornment={false}
                            />

                            <form onSubmit={handleSubmit(handleOnSubmitStep1)} autoComplete={"off"}>
                                <Box display="flex" flexWrap={"wrap"}>
                                    <Box pt={1} width="100%">
                                        <TextField
                                            error={!!errors.businessId}
                                            variant="outlined"
                                            margin="dense"
                                            id="businessId"
                                            label="Business ID*"
                                            {...register("businessId", {required: true, maxLength: 20, minLength: 6})}
                                            name="businessId"
                                            size="small"
                                            type="text"
                                            placeholder="Enter your Business ID"
                                            fullWidth={true}
                                            slotProps={{
                                                inputLabel: {
                                                    shrink: true,
                                                    disabled: false
                                                }
                                            }}
                                            disabled={integrationId > 0}
                                        />
                                    </Box>
                                </Box>
                            </form>
                        </Box>
                    </>
                }

                {integration?.smartnews?.activeStep === 2 &&
                    <>
                        <List>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText disableTypography
                                                    primary="Synchronize ad-level cost every 30 minutes."/>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText disableTypography
                                                    primary="Check the status of your campaigns, ad groups and ads."/>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText disableTypography
                                                    primary="Pause or resume campaigns, ad groups and ads."/>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText disableTypography
                                                    primary="Change daily budget for campaigns direclty from Skro."/>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText disableTypography
                                                    primary="Check delivery status of campaigns, ad groups and ads."/>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText disableTypography
                                                    primary="Import dynamic columns like CPM, Link CPC, Link CTR, Impressions, Link EPC and Link Clicks."/>
                            </CustomListItem>
                        </List>

                        {integration?.loading?.integrateAdAccount && <Spinner overComponentBox={true}/>}

                        <Box display="flex" flexWrap={"wrap"}>
                            <Box py={0.75} width="100%">
                                <ReactHookFormSelect
                                    rules={{required: true}}
                                    name="currency"
                                    control={control}
                                    setValue={setValue}
                                    label="Ad Account Currency*"
                                    placeholder="Your SmartNews account currency"
                                    defaultValue="JPY"
                                    options={[
                                        {"label": "EUR", "value": "EUR"},
                                        {"label": "USD", "value": "USD"},
                                        {"label": "JPY", "value": "JPY"}
                                    ]}
                                />
                            </Box>
                            <Box py={0.75} width="100%">
                                <ReactHookFormSelect
                                    rules={{required: true}}
                                    name="timezone"
                                    control={control}
                                    setValue={setValue}
                                    label="Ad Account Timezone*"
                                    placeholder="Your SmartNews ad account timezone"
                                    defaultValue="America/Los_Angeles"
                                    options={[
                                        {"label": "Asia/Tokyo", "value": "Asia/Tokyo"},
                                        {"label": "America/Los_Angeles", "value": "America/Los_Angeles"},
                                    ]}
                                />
                            </Box>
                        </Box>

                        <Box pt={2} pb={2}>SmartNews Ad Accounts</Box>
                        {
                            integration?.loading?.adAccounts ? (
                                <Spinner overComponentBox={true}/>
                            ) : (
                                <IntegrationAdAccountList provider="SmartNews">
                                    {integration?.adAccounts?.map((item) => {
                                        return (
                                            <IntegrationAdAccountRow key={item?.adAccountId} name={item?.adAccountName}
                                                accountId={item?.adAccountId} integrated={item?.integrated || adAccountId === parseInt(item?.adAccountId)}
                                                onIntegrate={() => {
                                                            setValue("adAccountId", parseInt(item?.adAccountId));
                                                            setValue("adAccountName", item?.adAccountName);
                                                            setAdAccountId(parseInt(item?.adAccountId));
                                                        }}/>
                                        );
                                    })}
                                </IntegrationAdAccountList>
                            )
                        }
                    </>
                }

                {integration?.smartnews?.activeStep === 3 &&
                    <Box sx={{position: "relative"}}>
                        {(integration?.loading?.conversionApi || integration?.loading?.integration) &&
                            <Spinner overComponentBox={true}/>}
                        <IntegrationConversionSettings provider="SmartNews" control={control} register={register} errors={errors}
                            enabled={watch("enableConversionAPI")} eventFields={fields}
                            onAddEvent={appendNewEvent} onDeleteEvent={handleDeleteIntegrationEvent}
                            pixelFields={pixelFields} onAddPixel={appendNewPixel} onDeletePixel={handleDeleteIntegrationPixel}/>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                <Box display="flex" justifyContent="flex-end">
                    <Box mr={3}>
                        <Button onClick={handleClose} color="inherit" variant="text">
                            Close
                        </Button>
                    </Box>
                    {integration?.smartnews?.activeStep === 1 &&
                        <Box>
                            <Button onClick={handleSubmit(handleOnSubmitStep1)} color="primary" variant="contained">
                                Next
                            </Button>
                        </Box>
                    }
                    {integration?.smartnews?.activeStep === 2 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(1)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmitStep2)} color="primary" variant="contained">
                                    Next
                                </Button>
                            </Box>
                        </>
                    }
                    {integration?.smartnews?.activeStep === 3 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(2)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmit)} color="primary" variant="contained">
                                    Save & Close
                                </Button>
                            </Box>
                        </>
                    }
                </Box>
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
    return {
        integration: state.integration,
        alert: state.alert
    };
};

const mapDispatchToProps = {
    resetIntegration: integrationActions.reset,
    find: integrationActions.findIntegration,
    setActiveStep: integrationActions.smartNewsSetActiveStep,
    getSmartNewsAdAccounts: integrationActions.getSmartNewsAdAccounts,
    smartNewsConnect: integrationActions.smartNewsConnect,
    integrateSmartNewsAdAccount: integrationActions.integrateSmartNewsAdAccount,
    integrateSmartNewsConversionAPI: integrationActions.integrateSmartNewsConversionAPI,
    error: alertActions.error,
    removeIntegrationEvent: integrationActions.removeIntegrationEventFromCurrentIntegration
};

export default connect(mapStateToProps, mapDispatchToProps)(SmartNewsIntegration);
